import React, { useState, useCallback, useEffect } from 'react';
import { theme } from '../theme';
import { BentoCard, CategoryBadge, LightningIcon, escapeHtmlText, linkifyUrls, styleRedactionTokens } from './shared';
import { useTicketManager } from '../contexts/TicketManagerContext';
import { useCategoryMeta } from '../context/ClassificationSchemaContext';
import {
  generateTestEmail, downloadTestEmail, emlBase64ToFile, listSignalAtoms, smarttix,
  GenerateTestEmailRequest, GeneratedEmail, SignalAtomEntry, UiWarps,
} from '../services/api';
import { formatBackendError } from '../utils/formatError';
import { ASSIGNABLE_CATEGORIES, EDIFACT_CATEGORIES, EDIFACT_FALLBACK_CATEGORY, TESTGEN_DEFAULT_TARGET_CATEGORY } from '../constants';
import { PageType } from './NavBar';
import { ROLE_DISPLAY_LABELS, isVnb } from '../services/roleRegistry';

// Operator ruling R36.5b (2026-09-28): the "Direkt hochladen" button's tooltip names the UPLOADS
// PAGE it drops the ticket into, not the served `bucket.uploads` word — fixed literal, not a served
// face (reverted from fix wave 2026-09-27, item 2, which had it read `face`/`configLoading`
// directly). A fixed page name needs neither argument, so the signature drops both.
export const IN_UPLOADS_ABLEGEN_TOOLTIP = 'In Uploads ablegen';

const CATEGORIES = [...ASSIGNABLE_CATEGORIES];
// Re-exported so edifactCategoryHelpers.test.ts's existing import path keeps working — home is
// constants.ts (EDIFACT_CATEGORIES/EDIFACT_FALLBACK_CATEGORY), this is a pass-through.
export { EDIFACT_CATEGORIES } from '../constants';
export const filterCategoriesForEdifact = (all: string[], edifact: boolean): string[] =>
  edifact ? all.filter(c => EDIFACT_CATEGORIES.includes(c)) : all;
export const sanitizeCategoryForEdifact = (current: string, edifact: boolean): string =>
  edifact && !EDIFACT_CATEGORIES.includes(current) ? EDIFACT_FALLBACK_CATEGORY : current;
export const sanitizeNoiseCategoryForEdifact = (current: string, edifact: boolean): string =>
  edifact && current !== '' && !EDIFACT_CATEGORIES.includes(current) ? '' : current;
const STRENGTHS = ['weak', 'medium', 'strong'];
const STRENGTH_LABELS: Record<string, string> = { weak: 'Schwach', medium: 'Mittel', strong: 'Stark' };

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 12px', fontSize: 13,
  border: `1px solid ${theme.colors.borderLight}`, borderRadius: theme.borderRadius.sm,
  fontFamily: theme.font.family, outline: 'none', boxSizing: 'border-box' as const,
};

const selectStyle: React.CSSProperties = { ...inputStyle, cursor: 'pointer', backgroundColor: theme.colors.white };

const btnStyle = (variant: 'primary' | 'ghost', disabled?: boolean): React.CSSProperties => ({
  padding: '8px 18px', fontSize: 13, fontWeight: 600, fontFamily: theme.font.family,
  border: variant === 'ghost' ? `1px solid ${theme.colors.borderLight}` : 'none',
  borderRadius: theme.borderRadius.sm, cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.5 : 1,
  color: variant === 'ghost' ? theme.colors.navy : theme.colors.white,
  backgroundColor: variant === 'primary' ? theme.colors.navy : 'transparent',
});

const fieldLabelStyle: React.CSSProperties = {
  fontSize: 12, fontWeight: 600, color: theme.colors.navyLight, fontFamily: theme.font.family,
};

const topicGroupStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 6,
  padding: `${theme.spacing.sm}px ${theme.spacing.md}px`,
  border: `1px solid ${theme.colors.borderLight}`,
  borderRadius: theme.borderRadius.sm,
  backgroundColor: theme.colors.bgPage,
};

// Tray with a downward arrow — "drop into uploads"
const UploadsArrowIcon: React.FC<{ size?: number; color?: string }> = ({ size = 13, color = 'currentColor' }) => (
  <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 15v3a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-3" />
    <polyline points="7 10 12 15 17 10" /><line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

const TopicCheckbox: React.FC<{
  label: string;
  checked: boolean;
  onChange?: (v: boolean) => void;
  disabled?: boolean;
  lockedHint?: string;
}> = ({ label, checked, onChange, disabled, lockedHint }) => (
  <label style={{
    display: 'flex', alignItems: 'center', gap: 8,
    fontSize: 13, fontFamily: theme.font.family,
    color: disabled ? theme.colors.navyMid : theme.colors.navy,
    cursor: disabled ? 'default' : 'pointer',
    userSelect: 'none',
  }}>
    <input
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={e => onChange?.(e.target.checked)}
      style={{ accentColor: theme.colors.navy, cursor: disabled ? 'default' : 'pointer' }}
    />
    <span style={{ fontWeight: checked ? 500 : 400 }}>{label}</span>
    {lockedHint && (
      <span style={{ fontSize: 11, color: theme.colors.textSecondary, fontStyle: 'italic' }}>
        ({lockedHint})
      </span>
    )}
  </label>
);

interface Props {
  role: string;
  onNavigate?: (page: PageType, params?: Record<string, string>) => void;
  uiWarps: UiWarps;
}

const TestgeneratorSeite: React.FC<Props> = ({ role, onNavigate, uiWarps }) => {
  // Same vocabulary seam the ticket surfaces read — this table headed its columns with K-vocabulary
  // regardless of role. 'Kat.' becomes the full noun rather than gaining an abbreviation FORM:
  // per the vocabulary's own tripwire, a fourth German form is the signal to stop assembling
  // strings, not to add another field.
  const cm = useCategoryMeta();
  const tm = useTicketManager();
  // Generator state
  const [edifact, setEdifact] = useState(false);
  const [targetCategory, setTargetCategory] = useState<string>(TESTGEN_DEFAULT_TARGET_CATEGORY);
  const [noiseCategory, setNoiseCategory] = useState('');
  const [mixedStrength, setMixedStrength] = useState('weak');
  // Boss feedback (2026-04-28): emails should have ONE main topic; secondary/tertiary are opt-in
  const [includeSecondary, setIncludeSecondary] = useState(false);
  const [includeTertiary, setIncludeTertiary] = useState(false);
  const [scenario, setScenario] = useState('');
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<GeneratedEmail | null>(null);
  const [error, setError] = useState('');

  const hasMixedCat = noiseCategory !== '';
  const availableCategories = filterCategoriesForEdifact(CATEGORIES, edifact);

  // Why: toggling EDIFACT mid-session must clear unsupported category selections
  // — otherwise targetCategory=K3A persists even though only K0/K1/K2 are valid
  // for EDIFACT, and the BE returns "no atoms found".
  const handleEdifactToggle = (next: boolean) => {
    setEdifact(next);
    setTargetCategory(prev => sanitizeCategoryForEdifact(prev, next));
    setNoiseCategory(prev => sanitizeNoiseCategoryForEdifact(prev, next));
  };

  // Atom reference (read-only)
  const [atoms, setAtoms] = useState<SignalAtomEntry[]>([]);
  const [atomsLoading, setAtomsLoading] = useState(false);
  const [showAtoms, setShowAtoms] = useState(false);

  const loadAtoms = useCallback(async () => {
    setAtomsLoading(true);
    try {
      const data = await listSignalAtoms(role);
      setAtoms(data);
    } catch { setAtoms([]); }
    setAtomsLoading(false);
  }, [role]);

  useEffect(() => { if (showAtoms) loadAtoms(); }, [showAtoms, loadAtoms]);

  const handleGenerate = async () => {
    setGenerating(true);
    setError('');
    setResult(null);
    try {
      const topicCount = 1 + (includeSecondary ? 1 : 0) + (includeTertiary ? 1 : 0);
      const req: GenerateTestEmailRequest = {
        // Why: edifact only meaningful on VNB. Defensive gate — if role prop
        // changes while edifact state persists, never send edifact=true to MSB.
        role, edifact: isVnb(role) && edifact, targetCategory,
        strength: 'medium',  // Hauptkategorie always uses medium; per-category strength was confusing UX
        topicCount,
        ...(hasMixedCat ? { noiseCategory, mixedStrength } : {}),
        ...(scenario ? { scenario } : {}),
      };
      const res = await generateTestEmail(req);
      setResult(res);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Fehler bei der Generierung');
    }
    setGenerating(false);
  };

  // ⚡ Bolt: ingest + process immediately (all defaults), land in AntwortenSeite — same end-state
  // as the Verarbeiten flow on UploadsSeite. Mirrors DashboardSeite Schnell-Upload bolt.
  const handleProcessDirectly = async () => {
    if (!result) return;
    setSending(true);
    setError('');
    try {
      const file = emlBase64ToFile(result.emlBase64, result.subject);
      const ingest = await smarttix.ingestTicket(file, { parseOnly: true });
      const proc = await tm.processTicket(ingest.uuid);
      if (!proc.success) throw new Error(proc.error || 'Fehler beim Verarbeiten');
      // Gated by uiWarps.testgenProcessToAntworten (#2) — operator can disable per-customer.
      if (onNavigate && uiWarps.testgenProcessToAntworten) onNavigate('responses', { ticket: ingest.uuid });
    } catch (e: any) {
      setError(formatBackendError(e, 'Direktes Verarbeiten'));
    }
    setSending(false);
  };

  // 📥 Direkt hochladen: ingest only, land in UploadsSeite for inspection.
  const handleSendToUploads = async () => {
    if (!result) return;
    setSending(true);
    setError('');
    try {
      const file = emlBase64ToFile(result.emlBase64, result.subject);
      const res = await smarttix.ingestTicket(file, { parseOnly: true });
      // Gated by uiWarps.testgenUploadToUploads (#2).
      if (onNavigate && uiWarps.testgenUploadToUploads) onNavigate('uploads', { ticket: res.uuid });
    } catch (e: any) {
      setError(formatBackendError(e, 'Hochladen'));
    }
    setSending(false);
  };

  const filteredAtoms = atoms.filter(a => {
    if (!a.isActive) return false;
    // Why: EDIFACT lives on the channel axis, not the utility axis. NB-EDIFACT
    // atoms carry utility='strom' + channel='edifact'.
    if (edifact) return a.channel === 'edifact';
    return true;
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md }}>
      <h1 className="visually-hidden">Testgenerator</h1>
      {/* Generator + Preview */}
      <div style={{ display: 'grid', gridTemplateColumns: result ? '1fr 1fr' : '1fr', gap: theme.spacing.md }}>
        <BentoCard title="Test-E-Mail generieren">
          <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
            <span style={fieldLabelStyle}>Domain</span>
            <div style={{ ...inputStyle, backgroundColor: theme.colors.bgLight, cursor: 'default' }}>
              {(ROLE_DISPLAY_LABELS as Record<string, string>)[role] || role.toUpperCase()}
            </div>

            {isVnb(role) && (
              <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input type="checkbox" checked={edifact} onChange={e => handleEdifactToggle(e.target.checked)} />
                EDIFACT-Marktprozesse
              </label>
            )}

            {/* ── Hauptkategorie + Themen ─────────────────────── */}
            <label htmlFor="testgen-hauptkategorie" style={fieldLabelStyle}>Hauptkategorie</label>
            <select id="testgen-hauptkategorie" value={targetCategory} onChange={e => setTargetCategory(e.target.value)} style={selectStyle}>
              {availableCategories.map(c => <option key={c} value={c}>{c} — {cm.label(c) || c}</option>)}
            </select>

            <span id="testgen-themen-label" style={fieldLabelStyle}>Themen aus Hauptkategorie</span>
            <div role="group" aria-labelledby="testgen-themen-label" style={topicGroupStyle}>
              <TopicCheckbox label="Hauptthema" checked disabled lockedHint="immer aktiv" />
              <TopicCheckbox label="Nebenthema" checked={includeSecondary} onChange={setIncludeSecondary} />
              <TopicCheckbox label="Drittes Thema" checked={includeTertiary} onChange={setIncludeTertiary} />
            </div>

            {/* ── Mischkategorie + Mischstärke (paired) ──────── */}
            <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: theme.spacing.sm }}>
              <div>
                <label htmlFor="testgen-mischkategorie" style={fieldLabelStyle}>Mischkategorie (optional)</label>
                <select id="testgen-mischkategorie" value={noiseCategory} onChange={e => setNoiseCategory(e.target.value)} style={selectStyle}>
                  <option value="">— Keine —</option>
                  {availableCategories.filter(c => c !== targetCategory).map(c =>
                    <option key={c} value={c}>{c} — {cm.label(c) || c}</option>
                  )}
                </select>
              </div>
              <div>
                <label htmlFor="testgen-mischstaerke" style={{
                  ...fieldLabelStyle,
                  color: hasMixedCat ? theme.colors.navyLight : theme.colors.textSecondary,
                }}>Mischstärke</label>
                <select
                  id="testgen-mischstaerke"
                  value={mixedStrength}
                  onChange={e => setMixedStrength(e.target.value)}
                  disabled={!hasMixedCat}
                  style={{
                    ...selectStyle,
                    borderStyle: hasMixedCat ? 'solid' : 'dashed',
                    opacity: hasMixedCat ? 1 : 0.5,
                    cursor: hasMixedCat ? 'pointer' : 'not-allowed',
                    color: hasMixedCat ? theme.colors.navy : theme.colors.textSecondary,
                  }}
                >
                  {STRENGTHS.map(s => <option key={s} value={s}>{STRENGTH_LABELS[s]}</option>)}
                </select>
              </div>
            </div>

            <label htmlFor="testgen-szenario" style={fieldLabelStyle}>Szenario (optional)</label>
            <textarea
              id="testgen-szenario"
              value={scenario} onChange={e => setScenario(e.target.value)}
              placeholder="z.B. 'Lieferant sendet fehlerhafte UTILMD mit falscher OBIS-Kennzahl'"
              rows={2} style={{ ...inputStyle, resize: 'vertical' }}
            />

            <button onClick={handleGenerate} disabled={generating} style={btnStyle('primary', generating)}>
              {generating ? 'Generiere...' : 'E-Mail generieren'}
            </button>

            {error && <div style={{ fontSize: 13, color: theme.colors.error }}>{error}</div>}
          </div>
        </BentoCard>

        {result && (
          <BentoCard title="Vorschau">
            <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{result.subject}</div>

              <div style={{ display: 'flex', gap: theme.spacing.sm, alignItems: 'center', flexWrap: 'wrap' }}>
                <CategoryBadge category={result.expectedCategory} />
                {result.signalsUsed.map((s, i) => (
                  <span key={i} style={{
                    fontSize: 11, padding: '2px 8px', borderRadius: 10,
                    backgroundColor: theme.colors.bgLight, color: theme.colors.navyDark,
                  }}>{s}</span>
                ))}
              </div>

              <div style={{
                fontSize: 13, lineHeight: 1.6, padding: theme.spacing.sm,
                backgroundColor: theme.colors.bgPage, borderRadius: theme.borderRadius.sm,
                border: `1px solid ${theme.colors.borderLight}`,
                maxHeight: 300, overflow: 'auto',
                whiteSpace: 'pre-wrap',  // honour LLM paragraph breaks; soft \n already collapsed server-side
                wordBreak: 'break-word',
              }}
                dangerouslySetInnerHTML={{ __html: styleRedactionTokens(linkifyUrls(escapeHtmlText(result.body))) }}
              />

              <div style={{ display: 'flex', gap: theme.spacing.sm, alignItems: 'center', flexWrap: 'wrap' }}>
                {/* ⚡ Direct-process — square icon button matching DashboardSeite Schnell-Upload bolt */}
                <button
                  onClick={handleProcessDirectly}
                  disabled={sending || !onNavigate}
                  title="Direkt verarbeiten (alle Standardwerte)"
                  style={{
                    padding: '4px 6px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: theme.colors.white, backgroundColor: theme.colors.navy,
                    border: 'none', borderRadius: theme.borderRadius.sm,
                    cursor: sending ? 'default' : 'pointer',
                    opacity: sending || !onNavigate ? 0.6 : 1,
                  }}
                >
                  <LightningIcon size={14} color={theme.colors.white} />
                </button>
                {/* 📥 Direkt hochladen — ghost, lets the user inspect/configure before processing */}
                <button
                  onClick={handleSendToUploads}
                  disabled={sending || !onNavigate}
                  title={IN_UPLOADS_ABLEGEN_TOOLTIP}
                  style={{
                    ...btnStyle('ghost', sending || !onNavigate),
                    display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px',
                  }}
                >
                  <UploadsArrowIcon size={13} color={theme.colors.navy} />
                  Direkt hochladen
                </button>
                {/* Download as fallback */}
                <button
                  onClick={() => downloadTestEmail(result.emlBase64, result.subject)}
                  style={{ ...btnStyle('ghost'), marginLeft: 'auto' }}
                >
                  .eml herunterladen
                </button>
              </div>
            </div>
          </BentoCard>
        )}
      </div>

      {/* Signal atom reference (read-only) */}
      <BentoCard title={`Signalvokabular (${role.toUpperCase()}${edifact ? ' / EDIFACT' : ''})`}>
        <button onClick={() => { setShowAtoms(!showAtoms); if (!showAtoms && atoms.length === 0) loadAtoms(); }}
          style={{ ...btnStyle('ghost'), fontSize: 12, padding: '4px 12px', alignSelf: 'flex-start' }}>
          {showAtoms ? 'Einklappen' : 'Signale anzeigen'}
        </button>
        {showAtoms && (
          atomsLoading ? (
            <div style={{ fontSize: 13, color: theme.colors.textSecondary, padding: theme.spacing.sm }}>Lade Signale...</div>
          ) : (
            <div style={{ maxHeight: 400, overflow: 'auto', marginTop: theme.spacing.sm }}>
              <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: `2px solid ${theme.colors.borderLight}`, textAlign: 'left' }}>
                    <th style={{ padding: '6px 8px' }}>{cm.categoryNoun}</th>
                    <th style={{ padding: '6px 8px' }}>{cm.secondaryAxisNoun}</th>
                    <th style={{ padding: '6px 8px' }}>Starke</th>
                    {isVnb(role) && <th style={{ padding: '6px 8px' }}>Utility</th>}
                    <th style={{ padding: '6px 8px' }}>Signaltext</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAtoms.map(a => (
                    <tr key={a.id} style={{ borderBottom: `1px solid ${theme.colors.bgLight}` }}>
                      <td style={{ padding: '4px 8px' }}><CategoryBadge category={a.category} style={{ fontSize: 10 }} /></td>
                      <td style={{ padding: '4px 8px', fontFamily: 'monospace', fontSize: 11 }}>{a.reasonCode}</td>
                      <td style={{ padding: '4px 8px' }}>{STRENGTH_LABELS[a.strength] || a.strength}</td>
                      {isVnb(role) && <td style={{ padding: '4px 8px', fontSize: 10, color: theme.colors.textSecondary }}>{a.utility || '—'}</td>}
                      <td style={{ padding: '4px 8px' }}>{a.atomText}</td>
                    </tr>
                  ))}
                  {filteredAtoms.length === 0 && (
                    <tr><td colSpan={isVnb(role) ? 5 : 4} style={{ padding: 16, textAlign: 'center', color: theme.colors.textSecondary, fontSize: 13 }}>
                      Keine Signale konfiguriert — Template-Sync via deploy-customer.sh --db-only
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )
        )}
      </BentoCard>
    </div>
  );
};

export default TestgeneratorSeite;
